import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../components/ui';
import FormLayoutRenderer from '../../components/generic/FormLayoutRenderer';
import { renderField, FieldConfig } from '../../components/generic/fieldRenderers';
import { useTheme } from '../../contexts/ThemeContext';
import { useNotification } from '../../contexts/NotificationContext';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import { useSupportAgreement, SupportAgreement, useServiceTypesForSelect } from './useData';
import {
  moduleConfig,
  getInitialFormData,
  mapItemToFormData,
} from './config';

const Form: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  const { agreement, isLoading: loadingData, mutate } = useSupportAgreement(isEdit ? parseInt(id!) : 0);
  const { options: serviceTypeOptions } = useServiceTypesForSelect();
  const { colors } = useTheme();
  const { showSuccess, showError } = useNotification();

  type FormSupportAgreement = Omit<SupportAgreement, 'clientId' | 'agreementTypeId' | 'categoryId'> & {
    clientId?: string | number;
    agreementTypeId?: string | number;
    categoryId?: string | number;
  };

  const [formData, setFormData] = useState<FormSupportAgreement>(() => {
    const initial = getInitialFormData();
    return {
      ...initial,
      clientId: '',
      agreementTypeId: '',
      categoryId: '',
    } as FormSupportAgreement;
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load data when editing
  useEffect(() => {
    if (isEdit && agreement) {
      const mappedData = mapItemToFormData(agreement);
      setFormData({
        ...mappedData,
        clientId: mappedData.clientId ? String(mappedData.clientId) : '',
        agreementTypeId: mappedData.agreementTypeId ? String(mappedData.agreementTypeId) : '',
        categoryId: mappedData.categoryId ? String(mappedData.categoryId) : '',
      } as FormSupportAgreement);
    }
  }, [isEdit, agreement]);

  // Filter fields based on agreementType
  const getFilteredFields = (fields: FieldConfig[], currentAgreementType?: SupportAgreement['agreementType']) => {
    return fields.filter((field) => {
      // If field has conditional property, check if it should be shown
      if ((field as any).conditional) {
        const conditional = (field as any).conditional;
        if (conditional.field === 'agreementType') {
          return currentAgreementType === conditional.value;
        }
      }
      return true;
    });
  };

  // Get filtered fields and sections based on current agreementType
  const { filteredFields, filteredSections } = useMemo(() => {
    const currentAgreementType = formData.agreementType;
    const filtered = getFilteredFields(moduleConfig.fields, currentAgreementType);
    
    // Add options to select fields
    const fieldsWithOptions = filtered.map((field) => {
      if (field.name === 'clientId') {
        return {
          ...field,
          options: [],
          apiEndpoint: '/crm-clients',
          labelField: 'clientName',
          valueField: 'clientId' as const,
        };
      }
      if (field.name === 'agreementTypeId') {
        return {
          ...field,
          options: serviceTypeOptions,
        };
      }
      if (field.name === 'categoryId') {
        return {
          ...field,
          options: [],
          apiEndpoint: '/p-crm-service-category',
          labelField: 'psc_descr',
          valueField: 'id' as const,
        };
      }
      return field;
    });

    // Filter form layout sections
    const filteredSections = moduleConfig.formLayout.sections.map((section) => {
      const visibleFields = section.fields.filter((fieldName) => {
        return fieldsWithOptions.some((f) => f.name === fieldName);
      });
      return {
        ...section,
        fields: visibleFields,
      };
    }).filter((section) => section.fields.length > 0);

    return {
      filteredFields: fieldsWithOptions,
      filteredSections,
    };
  }, [formData.agreementType, serviceTypeOptions]);

  const handleChange = (name: string, value: any) => {
    setFormData((prev) => {
      const newData = { ...prev, [name]: value };
      
      // When agreementType changes, clear type-specific fields
      if (name === 'agreementType' && value !== prev.agreementType) {
        // Clear MONTHLY fields
        if (value !== 'MONTHLY') {
          newData.monthlyFee = undefined;
          newData.includedHours = undefined;
          newData.overageRate = undefined;
        }
        // Clear AT_CALL fields
        if (value !== 'AT_CALL') {
          newData.hourlyRate = undefined;
          newData.minimumHours = undefined;
        }
        // Clear HOUR_PACKAGE fields
        if (value !== 'HOUR_PACKAGE') {
          newData.totalMinutes = undefined;
          newData.usedMinutes = undefined;
          newData.packagePrice = undefined;
        }
      }
      
      return newData;
    });
    
    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validate = (): boolean => {
    const errors: Record<string, string> = {};
    
    // Validate clientId - must be a non-empty value that can be converted to a number
    const clientIdValue = formData.clientId;
    if (!clientIdValue || clientIdValue === '' || (typeof clientIdValue === 'string' && isNaN(Number(clientIdValue)))) {
      errors.clientId = 'Client is required';
    }
    
    if (!formData.agreementType) {
      errors.agreementType = 'Agreement type is required';
    }
    if (!formData.description?.trim()) {
      errors.description = 'Description is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) {
      showError('Please fix the errors in the form');
      return;
    }

    setIsSubmitting(true);
    try {
      // Helper function to convert string to number or undefined
      const convertToNumber = (value: string | number | undefined): number | undefined => {
        if (value === undefined || value === null || value === '') {
          return undefined;
        }
        const num = Number(value);
        return isNaN(num) ? undefined : num;
      };

      // clientId is required, so ensure it's a number
      const clientIdNum = convertToNumber(formData.clientId);
      if (!clientIdNum) {
        setFormErrors({ clientId: 'Client is required' });
        setIsSubmitting(false);
        showError('Client is required');
        return;
      }

      const processedData: Partial<SupportAgreement> = {
        ...formData,
        clientId: clientIdNum, // Always a number when provided
        agreementTypeId: convertToNumber(formData.agreementTypeId),
        categoryId: convertToNumber(formData.categoryId),
        // Ensure numeric fields are numbers, not strings
        monthlyFee: formData.monthlyFee !== undefined && formData.monthlyFee !== null ? Number(formData.monthlyFee) : undefined,
        includedHours: formData.includedHours !== undefined && formData.includedHours !== null ? Number(formData.includedHours) : undefined,
        overageRate: formData.overageRate !== undefined && formData.overageRate !== null ? Number(formData.overageRate) : undefined,
        hourlyRate: formData.hourlyRate !== undefined && formData.hourlyRate !== null ? Number(formData.hourlyRate) : undefined,
        minimumHours: formData.minimumHours !== undefined && formData.minimumHours !== null ? Number(formData.minimumHours) : undefined,
        totalMinutes: formData.totalMinutes !== undefined && formData.totalMinutes !== null ? Number(formData.totalMinutes) : undefined,
        usedMinutes: formData.usedMinutes !== undefined && formData.usedMinutes !== null ? Number(formData.usedMinutes) : undefined,
        packagePrice: formData.packagePrice !== undefined && formData.packagePrice !== null ? Number(formData.packagePrice) : undefined,
        totalDue: formData.totalDue !== undefined && formData.totalDue !== null ? Number(formData.totalDue) : undefined,
      };

      if (isEdit) {
        await moduleConfig.form.update(parseInt(id!), processedData);
        showSuccess('Support agreement updated successfully');
      } else {
        await moduleConfig.form.create(processedData);
        showSuccess('Support agreement created successfully');
      }
      mutate();
      navigate(moduleConfig.module.route.list);
    } catch (error: any) {
      showError(error.message || 'Failed to save support agreement');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Pre-render all fields
  const renderedFields = useMemo(() => {
    const fields: Record<string, React.ReactNode> = {};
    filteredFields.forEach((fieldConfig) => {
      fields[fieldConfig.name] = renderField({
        field: fieldConfig,
        value: formData[fieldConfig.name as keyof FormSupportAgreement],
        onChange: handleChange,
        error: formErrors[fieldConfig.name],
      });
    });
    return fields;
  }, [formData, formErrors, filteredFields]);

  if (loadingData && isEdit) {
    return <LoadingSpinner />;
  }

  const formConfig = isEdit ? moduleConfig.form.edit : moduleConfig.form.new;

  return (
    <div className="h-full flex flex-col">
      <div className="max-w-4xl mx-auto w-full flex flex-col h-full">
        {/* Header */}
        <div className="flex-shrink-0 mb-6">
          <h1 className={`text-3xl font-bold ${colors.text}`}>{formConfig.title}</h1>
          {formConfig.subtitle && (
            <p className={`${colors.textSecondary} mt-1`}>{formConfig.subtitle}</p>
          )}
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className={`flex-1 flex flex-col ${colors.surface} rounded-lg shadow-sm border ${colors.border} overflow-hidden`}
        >
          {/* Scrollable Form Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <FormLayoutRenderer
              layout={{
                ...moduleConfig.formLayout,
                sections: filteredSections,
              }}
              fields={renderedFields}
              formData={formData}
            />
          </div>

          {/* Form Actions */}
          <div
            className={`flex-shrink-0 flex items-center justify-end space-x-3 p-6 border-t ${colors.border} bg-gray-50 dark:bg-gray-800`}
          >
            <Button type="button" onClick={() => navigate(moduleConfig.module.route.list)} variant="outline">
              {moduleConfig.form.cancelText}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : formConfig.submitText}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Form;
